import type { OrderRecord } from '@adiox/api';
import type { Position } from '../stream/accountModel';
import { pointSize } from './levels';
import { normalizeVolume, type Side } from './tradeRules';

// ------------------------------------------------------------------ sorting and totals of open positions

export type PositionSortKey =
  | 'ticket'
  | 'timeOpen'
  | 'symbol'
  | 'side'
  | 'volume'
  | 'priceOpen'
  | 'stopLoss'
  | 'takeProfit'
  | 'price'
  | 'commission'
  | 'swap'
  | 'profit'
  | 'comment';

export interface PositionSort {
  key: PositionSortKey;
  dir: 'asc' | 'desc';
}

export const DEFAULT_SORT: PositionSort = { key: 'ticket', dir: 'asc' };

/** Sorted copy; ties fall back to the ticket, and positions without a live figure yet sort last either way. */
export function sortPositions(list: readonly Position[], sort: PositionSort): Position[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    const x = sort.key === 'comment' ? (a.comment ?? '') : a[sort.key];
    const y = sort.key === 'comment' ? (b.comment ?? '') : b[sort.key];
    if (x === null || y === null) {
      if (x !== y) return x === null ? 1 : -1;
    } else if (x !== y) {
      return (typeof x === 'string' ? x.localeCompare(y as string) : (x as number) - (y as number)) * sign;
    }
    return a.ticket - b.ticket;
  });
}

/** The next sort after a click on a column: the same column flips, a new one starts ascending. */
export const nextSort = (current: PositionSort, key: PositionSortKey): PositionSort =>
  current.key === key ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' };

export interface PositionTotals {
  volume: number;
  commission: number;
  swap: number;
  /** Null until every position has its first valuation, so a partial sum is never shown as the total. */
  profit: number | null;
}

const cents = (v: number) => Math.round(v * 100) / 100;

export function positionTotals(list: readonly Position[]): PositionTotals {
  let volume = 0;
  let commission = 0;
  let swap = 0;
  let profit: number | null = 0;
  for (const p of list) {
    volume += p.volume;
    commission += p.commission;
    swap += p.swap;
    profit = profit === null || p.profit === null ? null : profit + p.profit;
  }
  return {
    volume: Math.round(volume * 1e8) / 1e8,
    commission: cents(commission),
    swap: cents(swap),
    profit: profit === null ? null : cents(profit),
  };
}

// ------------------------------------------------------------------ pending orders

/** The price that must be reached for a pending order to trigger: the stop price of a stop-limit, else the order price. */
const triggerOf = (order: OrderRecord) => order.priceOrder;

/**
 * How far a pending order is from triggering, in points (always positive). Buy orders trigger on the ask, sell orders on the
 * bid, as the server checks them. Null without a quote.
 */
export function pendingDistance(
  order: OrderRecord,
  quote: { bid: number; ask: number } | undefined,
  digits: number,
): number | null {
  if (!quote) return null;
  const market = order.type.startsWith('Buy') ? quote.ask : quote.bid;
  return Math.round(Math.abs(triggerOf(order) - market) / pointSize(digits));
}

// ------------------------------------------------------------------ risk and lot size

/** What the move from `entry` to `level` is worth for `volume` lots, as a positive amount of account money. */
export const moneyBetween = (entry: number, level: number, volume: number, moneyPerLot: number): number =>
  cents(Math.abs(level - entry) * volume * moneyPerLot);

/** The result at a level: negative when the level is on the losing side of the entry for this side. */
export function resultAt(
  side: Side,
  entry: number,
  level: number,
  volume: number,
  moneyPerLot: number,
): number {
  const move = side === 'Buy' ? level - entry : entry - level;
  return cents(move * volume * moneyPerLot);
}

export type LotSize = { ok: true; volume: number; risk: number } | { ok: false; message: string };

/**
 * The largest volume whose loss at the stop loss stays within `riskMoney`: rounded down to the volume step, never above the
 * symbol's maximum. `moneyPerLot` is account money per 1.0 of price movement for one lot.
 */
export function lotsForRisk(
  riskMoney: number,
  entry: number,
  stopLoss: number,
  moneyPerLot: number,
  info: { volumeMin: number; volumeMax: number; volumeStep: number },
): LotSize {
  if (!(riskMoney > 0)) return { ok: false, message: 'Enter the risk as a positive number.' };
  if (!(stopLoss > 0)) return { ok: false, message: 'Set a stop loss first: the risk is measured to it.' };
  const perLot = Math.abs(entry - stopLoss) * moneyPerLot;
  if (!(perLot > 0)) return { ok: false, message: 'The stop loss must be away from the entry price.' };
  const raw = riskMoney / perLot;
  if (raw < info.volumeMin - 1e-9) {
    return {
      ok: false,
      message: `Even the minimum ${info.volumeMin} lots would risk ${cents(perLot * info.volumeMin).toFixed(2)}. Move the stop loss closer or risk more.`,
    };
  }
  // Rounded down from the minimum in whole steps, so the risk is never exceeded; normalizeVolume only tidies the decimals.
  const steps = Math.floor((raw - info.volumeMin) / info.volumeStep + 1e-9);
  const volume = normalizeVolume(Math.min(info.volumeMin + steps * info.volumeStep, info.volumeMax), info);
  return { ok: true, volume, risk: cents(perLot * volume) };
}

/** Handy volumes for the quick buttons: the usual sizes this symbol accepts, at most five. */
export function quickVolumes(info: { volumeMin: number; volumeMax: number; volumeStep: number }): number[] {
  const candidates = [info.volumeMin, 0.01, 0.05, 0.1, 0.5, 1, 2, 5, 10];
  const fits = (v: number) =>
    v >= info.volumeMin &&
    v <= info.volumeMax &&
    Math.abs(v / info.volumeStep - Math.round(v / info.volumeStep)) < 1e-6;
  return [...new Set(candidates.filter(fits))].sort((a, b) => a - b).slice(0, 5);
}
